"use client"

import React from "react"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "../shadcn/modal"
import { cn } from "../../utils"

const sizeClasses = {
	xsmall: "max-w-[480px]",
	small: "max-w-[600px]",
	medium: "max-w-[800px]",
	large: "max-w-[1080px]",
	xlarge: "max-w-[1200px]"
}

/**
 * 일반 모달 다이얼로그
 *
 * @description
 * open/onOpenChange로 제어하는 모달입니다.
 * renderActions로 하단 버튼(저장/취소) 영역을 정의합니다.
 * children이 function이면 { onClose }를 받아 내부에서 모달을 닫을 수 있습니다.
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false)
 *
 * <TModal
 *   open={open}
 *   onOpenChange={setOpen}
 *   title="설정"
 *   size="medium"
 *   renderActions={({ onClose }) => (
 *     <>
 *       <TButton color="tertiary" onClick={onClose}>취소</TButton>
 *       <TButton onClick={() => { save(); onClose() }}>저장</TButton>
 *     </>
 *   )}
 * >
 *   <div>모달 내용</div>
 * </TModal>
 * ```
 */
interface TModalProps {
	/** 모달 열림 상태 (제어 필수) */
	open: boolean
	/** 열림 상태 변경 콜백 */
	onOpenChange: (open: boolean) => void
	/** 모달 제목 */
	title: string
	/**
	 * 모달 본문. ReactNode 또는 ({ onClose }) => ReactNode 형태
	 * function일 경우 onClose로 모달을 내부에서 닫을 수 있음
	 */
	children: ((props: { onClose: () => void }) => React.ReactNode) | React.ReactNode
	/** 제목 아래 설명 텍스트 */
	description?: string | React.ReactNode
	/** 모달을 여는 트리거 요소 */
	trigger?: React.ReactNode
	/**
	 * 하단 액션 버튼 영역
	 * @param props.onClose 모달 닫기 함수
	 */
	renderActions?: (props: { onClose: () => void }) => React.ReactNode
	/**
	 * 모달 크기
	 * @default "medium"
	 * @ai-style "xsmall" → 최대 너비 480px
	 * @ai-style "small" → 최대 너비 600px
	 * @ai-style "medium" → 최대 너비 800px
	 * @ai-style "large" → 최대 너비 1080px
	 * @ai-style "xlarge" → 최대 너비 1200px
	 */
	size?: "xsmall" | "small" | "medium" | "large" | "xlarge"
	/**
	 * 우측 상단 닫기(X) 버튼 노출 여부
	 * 진행 중인 작업처럼 임의 종료를 막아야 하는 모달에서 false로 지정
	 * @default true
	 */
	showCloseButton?: boolean
	/**
	 * 닫기(X) 버튼의 접근성 라벨. 패키지는 앱의 i18n에 접근할 수 없으므로 앱이 번역된 문자열을
	 * 직접 주입할 수 있게 한다.
	 * @default "Close dialog"
	 */
	closeLabel?: string
	/** 추가 CSS 클래스 */
	className?: string
}

export function TModal(props: TModalProps) {
	const { open, onOpenChange, title, children, description, trigger, renderActions, size = "medium", showCloseButton = true, closeLabel, className } = props

	const modalSizeClass = sizeClasses[size]

	const handleClose = () => onOpenChange(false)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
			<DialogContent className={cn(modalSizeClass, className)} showCloseButton={showCloseButton} closeLabel={closeLabel}>
				<DialogHeader>
					<DialogTitle>{title}</DialogTitle>
					{typeof description === "string" ? (
						<DialogDescription>{description}</DialogDescription>
					) : (
						<>
							<DialogDescription className="sr-only">{title}</DialogDescription>
							<div className="text-muted-foreground">{description}</div>
						</>
					)}
					{!description && <DialogDescription className="sr-only" />}
				</DialogHeader>
				{typeof children === "function" ? children({ onClose: handleClose }) : children}
				{renderActions && <DialogFooter>{renderActions({ onClose: handleClose })}</DialogFooter>}
			</DialogContent>
		</Dialog>
	)
}

TModal.displayName = "TModal"
